import type { CaptionModel } from "./captionModel";

export function normalizeTimestampForMatch(timestamp: string): string {
  return timestamp.trim().replace(",", ".");
}

export function findMatchingOriginalCaption(
  originals: CaptionModel[],
  cueIndex: number,
  start: string,
  end: string
): CaptionModel | undefined {
  if (originals[cueIndex]) {
    return originals[cueIndex];
  }

  const normStart = normalizeTimestampForMatch(start);
  const normEnd = normalizeTimestampForMatch(end);

  return (
    originals.find(
      (orig) =>
        normalizeTimestampForMatch(orig.start) === normStart &&
        normalizeTimestampForMatch(orig.dur) === normEnd
    ) ??
    originals.find((orig) => normalizeTimestampForMatch(orig.start) === normStart)
  );
}

/** Map speaker tags from original words onto translated tokens (export-only display). */
export function buildTranslatedWordsWithSpeakerTags(
  translatedText: string,
  originalWords?: CaptionModel["words"]
): CaptionModel["words"] | undefined {
  if (!originalWords?.length) {
    return undefined;
  }

  const tokens = translatedText.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) {
    return undefined;
  }

  return tokens.map((word, index) => {
    const position = tokens.length === 1 ? 0 : index / (tokens.length - 1);
    const originalIndex = Math.min(
      Math.round(position * (originalWords.length - 1)),
      originalWords.length - 1
    );
    const source = originalWords[originalIndex];
    return {
      word,
      speakerTag: source.speakerTag,
      startTime: source.startTime,
    };
  });
}

export function mergeTranslatedCaptionWithSpeakers(
  translatedText: string,
  matchingOriginal: CaptionModel | undefined,
  start: string,
  end: string
): CaptionModel {
  const text = translatedText.trim();

  if (!matchingOriginal) {
    return { start, dur: end, text };
  }

  return {
    start,
    dur: end,
    text,
    speakerTag: matchingOriginal.speakerTag,
    words: buildTranslatedWordsWithSpeakerTags(text, matchingOriginal.words),
  };
}

export function parseTranslatedWebVttWithSpeakers(
  webvttContent: string,
  originalCaptions: CaptionModel[]
): CaptionModel[] {
  if (!webvttContent?.trim() || !originalCaptions.length) {
    return [];
  }

  const translatedCaptions: CaptionModel[] = [];
  let cueIndex = 0;
  let currentTimestamp = "";
  let currentText = "";
  let isInCue = false;

  const flushCue = () => {
    if (!currentTimestamp || !currentText.trim()) {
      return;
    }

    const [start, end] = currentTimestamp.split("-->").map((part) => part.trim());
    const matchingOriginal = findMatchingOriginalCaption(
      originalCaptions,
      cueIndex,
      start,
      end
    );

    translatedCaptions.push(
      mergeTranslatedCaptionWithSpeakers(currentText, matchingOriginal, start, end)
    );
    cueIndex += 1;
    currentText = "";
    currentTimestamp = "";
    isInCue = false;
  };

  for (const rawLine of webvttContent.split("\n")) {
    const line = rawLine.trim();
    if (!line || line === "WEBVTT" || /^\d+$/.test(line)) {
      continue;
    }

    const timestampMatch = line.match(
      /(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[.,]\d{3})/
    );

    if (timestampMatch) {
      flushCue();
      currentTimestamp = line;
      isInCue = true;
      continue;
    }

    if (isInCue) {
      currentText += (currentText ? " " : "") + line;
    }
  }

  flushCue();
  return translatedCaptions;
}
